/**
 * Admin — liens partenaires (affiliation), activables section par section.
 *
 * Chaque partenaire (config affiliation_links.yaml) a un interrupteur. Par
 * défaut, les liens officiels sont actifs et les liens commerciaux sont
 * désactivés (à activer quand l'accord partenaire est réel). Toute bascule est
 * auditée côté serveur et filtre immédiatement ce que voient les utilisateurs.
 */
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import {
  getAdminAffiliations,
  setAdminAffiliation,
  type AdminAffiliation,
} from "@/api/admin";

export default function AdminAffiliationsPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "affiliations"],
    queryFn: getAdminAffiliations,
  });

  const mut = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      setAdminAffiliation(key, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "affiliations"] }),
  });

  const official = (data ?? []).filter((a) => a.is_official);
  const commercial = (data ?? []).filter((a) => !a.is_official);

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Link to="/admin" className="text-sm text-blue-900 underline">
          ← Admin
        </Link>
        <h1 className="mt-3 text-2xl font-bold">Liens partenaires</h1>
        <p className="mt-1 text-sm text-gray-600">
          Activez ou désactivez chaque section de liens. L'effet est immédiat
          pour les utilisateurs.
        </p>

        {isLoading && <p className="mt-6 text-sm text-gray-500">Chargement…</p>}
        {error && (
          <p className="mt-6 text-sm text-red-700">Impossible de charger les partenaires.</p>
        )}

        {data && (
          <>
            <Group
              title="Liens officiels (institutionnels)"
              items={official}
              busy={mut.isPending}
              onToggle={(key, enabled) => mut.mutate({ key, enabled })}
            />
            <Group
              title="Liens commerciaux (affiliation)"
              items={commercial}
              busy={mut.isPending}
              onToggle={(key, enabled) => mut.mutate({ key, enabled })}
            />
          </>
        )}
      </div>
    </Layout>
  );
}

function Group({
  title,
  items,
  busy,
  onToggle,
}: {
  title: string;
  items: AdminAffiliation[];
  busy: boolean;
  onToggle: (key: string, enabled: boolean) => void;
}) {
  if (items.length === 0) return null;
  return (
    <section className="mt-6">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </h2>
      <div className="mt-2 space-y-2">
        {items.map((a) => (
          <div
            key={a.key}
            className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4"
          >
            <div>
              <div className="text-sm font-medium">{a.label}</div>
              <div className="font-mono text-xs text-gray-400">{a.key}</div>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={() => onToggle(a.key, !a.enabled)}
              aria-pressed={a.enabled}
              className={`rounded-full px-3 py-1 text-sm font-semibold disabled:opacity-50 ${
                a.enabled ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"
              }`}
            >
              {a.enabled ? "Activé" : "Désactivé"}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
